<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <b-row>
      <b-col sm="6">
        <admin-breadcrumb :items="[{ text: 'Shipping', path: {name: 'admin-shipping'} }]" name="Shipper Prices"/>
      </b-col>
      <b-col sm="6" class="text-right align-self-end">
        <no-permission-wrapper :show="!canUpdateShippers">
          <b-btn variant="success" @click.prevent="openNewPriceModal" class="mb-4" :disabled="!canUpdateShippers">Add New Shipper Price</b-btn>
        </no-permission-wrapper>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="12">
        <admin-panel title="Shipper Prices" sub-title="Manage all shipper prices created in GreenLists" with-footer>
          <admin-shipper-prices-table :prices="prices" @update="updatePrice" @delete="deletePrice"/>
          <template v-slot:header-right>
            <b-form-select
                class="w-auto ml-2"
                v-model="sorting"
                :options="sortingOptions"
                @change="() => loadPrices()"
            />
            <b-form-select
                class="w-auto ml-2"
                v-model="filters.shipper"
                :options="shipperOptions"
                @change="() => loadPrices()"
            />
            <b-form-select
                class="w-auto ml-2"
                v-model="filters.destination_state"
                :options="stateOptions"
                @change="() => loadPrices()"
            />
          </template>
          <template v-slot:footer>
            <b-pagination
                v-model="page"
                :total-rows="total_in_pages"
                :per-page="per_page"
                @change="loadPrices"
                align="center"
                class="admin-pagination mb-0"
            />
          </template>
        </admin-panel>
      </b-col>
    </b-row>
    <admin-shipper-price-new-modal
        ref="newPriceModal"
        :shippers="shippers"
        @created="newPriceCreated"
    />
    <admin-shipper-price-edit-modal
        :shipper-price="priceToEdit"
        ref="editPriceModal"
        @updated="priceUpdated"
    />
  </b-container>
</template>

<script>
import {API_ROUTES} from "../api";
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import AdminShipperPricesTable from "../components/AdminShipperPricesTable";
import {STATES_OPTIONS} from "../constants";
import AdminShipperPriceNewModal from "../components/AdminShipperPriceNewModal";
import AdminShipperPriceEditModal from "../components/AdminShipperPriceEditModal";
import NoPermissionWrapper from "../components/NoPermissionWrapper.vue";

export default {
  name: "AdminShipperPrices",
  components: {
    NoPermissionWrapper,
    AdminShipperPriceEditModal,
    AdminShipperPriceNewModal, AdminShipperPricesTable, AdminPanel, AdminBreadcrumb, AdminPreloader},
  data() {
    let stateOptions = [...STATES_OPTIONS];
    stateOptions.unshift({
      value: "",
      text: "All States"
    });

    return {
      prices: [],
      loading: true,
      shippers: [],
      filters: {
        shipper: "",
        destination_state: ""
      },
      stateOptions,
      priceToEdit: {},
      page: 1,
      per_page: 10,
      total_in_pages: 0,
      sorting: ""
    }
  },
  created() {
    this.loadPrices();
    this.$api.get(API_ROUTES.ADMIN_SHIPPERS, {
      params: {
        is_active: 1
      }
    }).then(({data}) => {
      this.shippers = data.shippers;
    });
  },
  methods: {
    loadPrices(page = 1) {
      this.loading = true;
      let params = {
        page: page,
        per_page: this.per_page
      };
      if (this.filters.shipper) {
        params['shipper'] = this.filters.shipper;
      }
      if (this.filters.destination_state) {
        params['destination_state'] = this.filters.destination_state;
      }
      if (this.search) {
        params['search'] = this.search;
      }
      if (this.sorting) {
        params['sorting'] = this.sorting;
      }
      this.$api.get(API_ROUTES.ADMIN_SHIPPER_PRICES, {
        params
      }).then(({data}) => {
        this.prices = data.shipper_prices;
        this.total_in_pages = data.meta.total;
      }).catch(({response: {data}}) => {

      }).finally(() => {
        if (page !== this.page) {
          this.page = page;
        }
        this.loading = false;
      });
    },
    openNewPriceModal() {
      this.$refs.newPriceModal.open();
    },
    newPriceCreated(shipperPrice) {
      this.prices.push(shipperPrice);
      this.$refs.newPriceModal.close();
    },
    updatePrice(shipperPrice) {
      this.priceToEdit = shipperPrice;
      this.$refs.editPriceModal.open();
    },
    priceUpdated(shipperPrice) {
      let prices = [...this.prices],
          priceIndex = prices.findIndex(price => price.id === shipperPrice.id);
      if (priceIndex >= 0) {
        prices[priceIndex] = shipperPrice;
      } else {
        prices.push(shipperPrice);
      }
      this.prices = prices;
      this.$refs.editPriceModal.close();
    },
    deletePrice(shipperPrice) {
      this.$bvModal.msgBoxConfirm("You are going to delete this shipper price", {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_SHIPPER_PRICES}/${shipperPrice.id}/delete`).then(() => {
            this.$bvToast.toast('Shipper price has been successfully deleted', {
              title: 'Success!',
              variant: 'success'
            });
            let prices = [...this.prices],
                priceIndex = prices.findIndex(price => price.id === shipperPrice.id);
            if (priceIndex >= 0) {
              prices.splice(priceIndex, 1);
            }
            this.prices = prices;
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    }
  },
  computed: {
    canUpdateShippers() {
      return this.$store.getters['auth/hasAdminPermissions']('shipper_update');
    },
    shipperOptions() {
      let options = this.shippers.map(shipper => ({
        value: shipper.id,
        text: `${shipper.name} - ${shipper.license_number}`
      }));
      options.unshift({
        value: "",
        text: "All Shippers"
      });

      return options;
    },
    search() {
      return this.$store.state.current.search;
    },
    sortingOptions() {
      let options = [
        {
          value: "",
          text: "Default Sorting"
        },
        {
          value: "price_asc",
          text: "Price Up"
        },
        {
          value: "price_desc",
          text: "Price Down"
        },
        {
          value: "shipper_name_asc",
          text: "Shipper A to Z"
        },
        {
          value: "shipper_name_desc",
          text: "Shipper Z to A"
        },
        {
          value: "destination_state_asc",
          text: "State A to Z"
        },
        {
          value: "destination_state_desc",
          text: "State Z to A"
        },
        {
          value: "destination_city_asc",
          text: "City A to Z"
        },
        {
          value: "destination_city_desc",
          text: "City Z to A"
        },
      ];
      if (this.filters.destination_state) {
        options.splice(options.findIndex(o => o.value === 'destination_state_asc'), 1);
        options.splice(options.findIndex(o => o.value === 'destination_state_desc'), 1);
      }
      if (this.filters.shipper) {
        options.splice(options.findIndex(o => o.value === 'shipper_name_asc'), 1);
        options.splice(options.findIndex(o => o.value === 'shipper_name_desc'), 1);
      }

      return options;
    }
  },
  watch: {
    search() {
      this.loadPrices();
    },
    'filters.destination_state': function (state) {
      this.$refs.newPriceModal.set('destination_state', state);
      if (state &&
          this.sorting &&
          ['destination_state_asc', 'destination_state_desc'].indexOf(this.sorting) >= 0
      ) {
        this.sorting = "";
        this.loadPrices();
      }
    },
    'filters.shipper': function (shipperId) {
      this.$refs.newPriceModal.set('shipper', shipperId);
      if (shipperId &&
          this.sorting &&
          ['shipper_name_asc', 'shipper_name_desc'].indexOf(this.sorting) >= 0
      ) {
        this.sorting = "";
        this.loadPrices();
      }
    }
  }
}
</script>
